import { BodyMedium, theme, DeprecatedInfoIcon } from '@arnold/common';
import styled from '@emotion/styled';
import { contains } from 'ramda';
import { MutableRefObject, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RouteComponentProps, useLocation, useParams, useRouteMatch, withRouter } from 'react-router-dom';
import { Loading } from '../components';
import client from '../createApollo';
import {
  AnonymityLevel,
  Features,
  FeEventType,
  ReportOpenMetricsView,
  ReportSequenceRespondentDetailQueryQuery,
  ReportSubscriptionSubscription,
  SurveyStatus,
  TopicGroupTypeCode,
  useLogFeEventMutation,
  useReportSequenceRespondentDetailQueryLazyQuery,
} from '../generated/hooks';
import { composeSearchParamsString } from '../lib/common';
import {
  getAlertsData,
  getPrintableReportMonth,
  getTeamNameByRespondentId,
  getTopicGroupTranslation,
} from '../lib/reportHelper';
import { exportPdf } from '../lib/reports/exportPdf';
import { PageTitleAndFavicon } from '../utils';
import { LOGIN_NEEDED, SOMEONE_ELSE_REPORT } from '../graphql/errors';
import auth from '../lib/auth';
import { buildReportGTMDimensions, ReportGTMContext } from './ReportGTMContext';
import { Conversation } from './components/Conversation';
import { Error } from './components/Error';
import { ReportOpenMetricsSender } from './components/ObserverEventSender';
import ReportContainer from './components/ReportContainer';
import { ReportMetrics } from './components/ReportMetrics';
import ReportSection from './components/ReportSection';
import RespondentDetailFeelings from './components/RespondentDetailFeelings';
import RespondentsFeelings from './components/RespondentsFeelings';
import { ResponsiveSwitcher } from './components/ResponsiveSwitcher';
import { useMetadataSetter, useReportSatismeter, useSendOpenReportToIntercom } from './components/hooks';
import RespondentSummary from './sequence/RespondentSummary';
import { SCROLL_TO_RESPONDENT_TABLE_PARAM } from '../lib/reports/reportUtils';

interface IProps {
  report: ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail'];
  selectedLanguage: string;
  changeLanguage: (lang: string) => any;
  setReportRespondent?: (
    respondent?: NonNullable<ReportSubscriptionSubscription['getReport']['metadata']>['respondent'],
  ) => void;
  publicAccess?: boolean;
  isSecuredDetail?: boolean;
  onReload: () => void;
}

const InfoBox = styled.div`
  padding: ${theme.spacing.f};
  border-radius: ${theme.borderRadius.m};
  border: 1px solid ${theme.colors.borderMain.default};
  & svg path {
    fill: ${theme.colors.actionPrimary.default};
  }
  & p {
    margin-left: ${theme.spacing.f};
  }
  margin-bottom: ${theme.spacing.g};
  display: flex;
`;

export const RespondentDetail = ({
  report,
  setReportRespondent,
  changeLanguage,
  selectedLanguage,
  publicAccess,
  onReload,
  isSecuredDetail,
}: IProps) => {
  const { t, i18n } = useTranslation('Report');
  const { token, respondentId } = useParams<{ token: string; respondentId: string }>();
  const [logFeEvent] = useLogFeEventMutation();

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search.substring(1) || '');
  const selectedTopicGroupId = searchParams.get('selected_tg');
  const addedOnFrom = searchParams.get('added_on_from') || undefined;
  const addedOnTo = searchParams.get('added_on_to') || undefined;
  const unitFilters = searchParams.get('unit-teams') || undefined;
  const positionIds = searchParams.get('position') || undefined;
  const recruiterIds = searchParams.get('recruiter') || undefined;
  const managerIds = searchParams.get('manager') || undefined;
  const page = searchParams.get('page') || undefined;

  const scrollRef = useRef() as MutableRefObject<HTMLDivElement>;

  const respondent = report.respondents[0];
  const handleLogFeEvent = (eventType: FeEventType) => {
    try {
      logFeEvent({
        variables: {
          eventType,
          payload: {
            reportAccessId: report.id,
            respondentId: respondent!.id,
          },
        },
      });
    } catch (err) {
      // Do nothing
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (selectedTopicGroupId && scrollRef.current) {
        scrollRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 0);
  }, [selectedTopicGroupId]);

  useMetadataSetter(
    changeLanguage,
    selectedLanguage,
    report.metadata.language && report.metadata.language.code,
    report.metadata.respondent as NonNullable<ReportSubscriptionSubscription['getReport']['metadata']>['respondent'],
    setReportRespondent,
  );

  useSendOpenReportToIntercom(!!publicAccess, report.metadata.respondent);

  if (!respondent) {
    return <Error>{t('respondentNotFound')}</Error>;
  }

  const team = report.teams.find((team) => contains(respondent.id, team.directMembers || []));
  const teamName = getTeamNameByRespondentId(report, respondent.id, t);

  if (!report.surveyGroup) {
    return null;
  }

  const accessReportSequencePath = publicAccess ? 'process-results' : 'report-sequence';
  const urlParams = composeSearchParamsString({
    added_on_from: addedOnFrom,
    added_on_to: addedOnTo,
    'unit-teams': unitFilters,
    position: positionIds,
    recruiter: recruiterIds,
    manager: managerIds,
    page,
  });

  const urlParamsForGoingBack = composeSearchParamsString({
    added_on_from: addedOnFrom,
    added_on_to: addedOnTo,
    'unit-teams': unitFilters,
    position: positionIds,
    recruiter: recruiterIds,
    manager: managerIds,
    page,
    [SCROLL_TO_RESPONDENT_TABLE_PARAM]: 'true',
  });

  const isHiring = report.surveyGroup.topicGroup.typeCode === TopicGroupTypeCode.Hiring;
  const topicGroupTranslation = getTopicGroupTranslation(report.surveyGroup.topicGroup, report.metadata.language).value;
  const scale = report.questions.find((q) => q.metrics?.[0] === 'onboarding_feeling')?.answersLocal?.length;

  const createDetailUrl = !isSecuredDetail
    ? undefined
    : (topicGroupId: string) => `/respondent-detail/${token}?selected_tg=${topicGroupId}`;
  const gtmDimensions = buildReportGTMDimensions({
    organizationId: report.metadata.organization.id,
    organizationName: report.metadata.organization.name,
    reportName: topicGroupTranslation,
    reportType: 'Onboarding',
  });

  return (
    <ReportGTMContext.Provider value={gtmDimensions}>
      <ReportContainer
        date={`${getPrintableReportMonth(report, report.metadata.language && report.metadata.language.code)}`}
        rootTeams={team ? [team] : []}
        title={`${respondent.firstname} ${respondent.surname}`}
        about={null}
        breadcrumb={[
          {
            title: topicGroupTranslation,
            link: isSecuredDetail
              ? undefined
              : `/${accessReportSequencePath}/${report.identifier}${urlParamsForGoingBack}`,
          },
          { title: `${respondent.firstname} ${respondent.surname}` },
        ]}
        organizationName={report.metadata.organization.name}
        respondentName={
          report.metadata?.respondent
            ? `${report.metadata.respondent.firstname} ${report.metadata.respondent.surname}`
            : undefined
        }
        exportPdf={() => {
          exportPdf(respondent, topicGroupTranslation, report, t, i18n.language, respondent.id, team, undefined, () =>
            handleLogFeEvent(FeEventType.DownloadRespondentDetail),
          );
        }}
      >
        <section className={'section'}>
          {publicAccess && <ReportMetrics reportAccessKey={report.identifier} trackTime />}
          <div className={'container container--md'}>
            <RespondentSummary report={report} />
            {!report.surveyGroup.topicGroup.hideOnboardingParts && (
              <ReportSection
                id={'respondentsTable'}
                header={isHiring ? t('respondentsHiringTable') : t('respondentsTable')}
              >
                {!isHiring && (
                  <InfoBox>
                    <div>
                      <DeprecatedInfoIcon />
                    </div>
                    <BodyMedium>
                      <Trans
                        i18nKey="Report:infoBoxAboutFeelingQuestion"
                        components={[<strong />]}
                        values={{ max: scale }}
                      />
                    </BodyMedium>
                  </InfoBox>
                )}
                <ResponsiveSwitcher
                  mobile={
                    <RespondentDetailFeelings
                      groupedRespondents={report.groupedRespondents}
                      reportSequence={report}
                      accessToken={report.identifier}
                      urlParams={urlParams}
                      encryptedRespondentId={respondentId}
                      publicAccess={publicAccess}
                      createDetailUrl={createDetailUrl}
                      showDeleteButton={
                        report.surveyGroup.steps.every(
                          (step) =>
                            !step.processStep.stepTopicGroup.allowedAnonymities?.includes(AnonymityLevel.Organization),
                        ) &&
                        (report.metadata.organization.featureNames!.includes(
                          Features.ReportRemoveRespondentFromProcessSurvey,
                        ) ||
                          !report.metadata.respondent)
                      }
                      onReload={onReload}
                      isDetail
                    />
                  }
                  desktop={
                    <RespondentsFeelings
                      groupedRespondents={report.groupedRespondents}
                      reportSequence={report}
                      accessToken={report.identifier}
                      urlParams={urlParams}
                      encryptedRespondentId={respondentId}
                      createDetailUrl={createDetailUrl}
                      publicAccess={publicAccess}
                      showDeleteButton={
                        report.surveyGroup.steps.every(
                          (step) =>
                            !step.processStep.stepTopicGroup.allowedAnonymities?.includes(AnonymityLevel.Organization),
                        ) &&
                        (report.metadata.organization.featureNames!.includes(
                          Features.ReportRemoveRespondentFromProcessSurvey,
                        ) ||
                          !report.metadata.respondent)
                      }
                      onReload={onReload}
                      isDetail
                    />
                  }
                />
              </ReportSection>
            )}
            <h2 className={'mb-0'}>{t('chats')}</h2>
            {report.surveyGroup.steps.map((step) => {
              const chat = getAlertsData(report, t, false, true).filter(
                (chat) =>
                  chat.messages.length &&
                  chat.respondentId === respondent.id &&
                  chat.topicGroupId === step.processStep.stepTopicGroup.id,
              )[0];

              const survey = step.surveys.find((r) =>
                chat ? r.id === chat.surveyId : r.seqDate === respondent.addedOn,
              );
              const surveyIsNotFinished =
                survey &&
                [
                  SurveyStatus.Ongoing,
                  SurveyStatus.Activating,
                  SurveyStatus.Prepared,
                  SurveyStatus.Notstarted,
                ].includes(survey.status);

              const channelId = report.groupedRespondents
                .find((gr) => gr.respondents.some((r) => r.id === respondent.id))
                ?.surveys.find((s) => s.id === survey?.id)
                ?.publicChannels.find((ch) => ch.respondentId === respondent.id && ch.withAnswer)?.id;
              const stepTopicGroupTranslation = getTopicGroupTranslation(
                step.processStep.stepTopicGroup,
                report.metadata.language,
              ).value;

              return (
                <div
                  ref={step.processStep.stepTopicGroup.id === selectedTopicGroupId ? scrollRef : undefined}
                  id={step.processStep.stepTopicGroup.id}
                  className={'my-8'}
                >
                  <Conversation
                    report={report}
                    allowedAnonymities={step.processStep.stepTopicGroup.allowedAnonymities}
                    messages={chat ? chat.messages : []}
                    team={
                      <>
                        {teamName.teamLabel}:&nbsp;{teamName.teamName}
                      </>
                    }
                    notFinished={surveyIsNotFinished}
                    name={getTopicGroupTranslation(step.processStep.stepTopicGroup, report.metadata.language, '').value}
                    endsAt={chat ? chat.date : survey ? new Date(survey.endsAt) : null}
                    startsAt={survey?.sendAt}
                    exportPdf={() =>
                      exportPdf(
                        respondent,
                        stepTopicGroupTranslation,
                        report,
                        t,
                        i18n.language,
                        respondent.id,
                        team,
                        step,
                        () => handleLogFeEvent(FeEventType.DownloadRespondentPartialDetail),
                      )
                    }
                  />
                  <ReportOpenMetricsSender
                    reportAccessKey={report.identifier}
                    orgAdmin={!publicAccess}
                    channelIds={channelId ? [channelId] : []}
                    reportView={ReportOpenMetricsView.RespondentDetail}
                  />
                </div>
              );
            })}
          </div>
          <PageTitleAndFavicon title={topicGroupTranslation} />
        </section>
      </ReportContainer>
    </ReportGTMContext.Provider>
  );
};

interface IReportSequenceLoaderProps extends RouteComponentProps<any> {
  accessToken: string;
  selectedLanguage: string;
  changeLanguage: (lang: string) => any;
  setReportRespondent?: (
    respondent?: NonNullable<ReportSubscriptionSubscription['getReport']['metadata']>['respondent'] | null,
  ) => void;
  setAllowedLanguages: (languages: string[]) => void;
  allowedLanguages?: string[];
}

const RespondentDetailLoader = (props: IReportSequenceLoaderProps) => {
  const { t } = useTranslation('Report');
  const { path } = useRouteMatch();
  const { respondentId, seqDate } = useParams<{ token: string; respondentId: string; seqDate: string }>();

  const [getReportSequenceRespondentDetailQuery, { data, loading, error }] =
    useReportSequenceRespondentDetailQueryLazyQuery({
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        props.changeLanguage(data.getProcessReportRespondentDetail.metadata.language!.code);
      },
      onError: (error) => {
        if (error.message === LOGIN_NEEDED) {
          auth.redirectToLogin();
        }
      },
    });
  const publicAccess = path.startsWith('/process-results/');

  useEffect(() => {
    if (props.selectedLanguage !== data?.getProcessReportRespondentDetail.metadata.language!.code) {
      getReportSequenceRespondentDetailQuery({
        variables: {
          accessToken: props.accessToken,
          languageCode: props.selectedLanguage,
          publicAccess,
          respondentId: publicAccess ? decodeURIComponent(respondentId) : respondentId,
          seqDate,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedLanguage]);

  useReportSatismeter(data?.getProcessReportRespondentDetail.metadata, publicAccess);

  useEffect(() => {
    if (data || loading) {
      getReportSequenceRespondentDetailQuery({
        variables: {
          accessToken: props.accessToken,
          languageCode: props.selectedLanguage,
          publicAccess,
          respondentId,
          seqDate,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respondentId]);

  if (loading) {
    return <Loading />;
  }

  if (!loading && error) {
    return <Error>{t(error.message === SOMEONE_ELSE_REPORT ? 'someoneElseReport' : 'errorLoading')}</Error>;
  }
  if (!data || (!data.getProcessReportRespondentDetail.metadata.accepted && publicAccess)) {
    return <Error>{t('errorLoading')}</Error>;
  }

  return (
    <RespondentDetail
      report={data.getProcessReportRespondentDetail}
      selectedLanguage={props.selectedLanguage}
      changeLanguage={props.changeLanguage}
      setReportRespondent={props.setReportRespondent}
      publicAccess={publicAccess}
      onReload={() => client.refetchQueries({ include: ['ReportSequenceRespondentDetailQuery'] })}
    />
  );
};

export default withRouter(RespondentDetailLoader);
